// Centralized analytics tracker — one import, consistent event names, auto-batched + auto-flushed.
// Works on web, Android (Capacitor), and iOS (Capacitor) — platform is auto-detected by backend.
// No PII ever — only IDs, names, counts, costs.

import axios from 'axios';
import { getAttribution, getAttributionHeaderValue } from './attribution';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUFFER_LIMIT = 20;            // flush when buffer reaches this many events
const FLUSH_INTERVAL_MS = 10000;    // flush every 10s whichever comes first
const STORAGE_KEY = 'analytics_session_id';

// Mirror of backend/analytics.py EventNames — keep them in sync
export const EVENT = {
  // Lifecycle
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_SUCCESS: 'signup_success',
  SIGNUP_FAILURE: 'signup_failure',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILURE: 'login_failure',
  LOGOUT: 'logout',
  // Reading
  CHAPTER_OPENED: 'chapter_opened',
  CHAPTER_COMPLETED: 'chapter_completed',
  CHAPTER_TIME_SPENT: 'chapter_time_spent',
  // Monetization
  CHAPTER_UNLOCK: 'chapter_unlock',
  BUNDLE_VIEWED: 'bundle_viewed',
  BUNDLE_PURCHASED: 'bundle_purchased',
  COIN_BALANCE_CHANGED: 'coin_balance_changed',
  AUDIO_UNLOCK: 'audio_unlock',
  AUDIO_PLAY_STARTED: 'audio_play_started',
  // Growth
  REFERRAL_POPUP_VIEWED: 'referral_popup_viewed',
  REFERRAL_LINK_SHARED: 'referral_link_shared',
  REFERRAL_REWARD_TRIGGERED: 'referral_reward_triggered',
  SOCIAL_REWARD_CLAIMED: 'social_reward_claimed',
  // Failures
  API_FAILURE: 'api_failure',
  UNLOCK_FAILURE: 'unlock_failure',
  PURCHASE_FAILURE: 'purchase_failure',
  AUTH_CALLBACK_FAILURE: 'auth_callback_failure',
};

// Stable session id (rotates only on browser restart)
const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(STORAGE_KEY, sid);
    }
    return sid;
  } catch {
    return `s_${Date.now()}`;
  }
};

// Detect platform client-side as a hint; backend re-detects from UA as the authoritative value
const detectPlatform = () => {
  try {
    if (window.Capacitor?.isNativePlatform?.()) {
      return window.Capacitor.getPlatform?.() || 'mobile'; // 'android' or 'ios'
    }
  } catch {}
  return 'web';
};

let buffer = [];
let flushTimer = null;

const flush = async () => {
  if (buffer.length === 0) return;
  const events = buffer.splice(0, buffer.length);
  try {
    const token = localStorage.getItem('token');
    const attrHeader = getAttributionHeaderValue();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (attrHeader) headers['x-sfy-attribution'] = attrHeader;
    await axios.post(`${API}/analytics/events`, { events }, {
      headers,
      timeout: 5000,
    });
  } catch {
    // silently drop — analytics must NEVER break UX.
    // Buffer is already drained; if a batch fails, those events are lost (acceptable tradeoff).
  }
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
};

/**
 * Track an event. Fire-and-forget — never throws, never blocks UX.
 * @param {string} eventName — use one of EVENT.* constants
 * @param {object} props — small dict of primitives (string/number/boolean). No PII.
 */
export const track = (eventName, props = {}) => {
  try {
    if (!eventName) return;
    const userId = localStorage.getItem('current_user_id') || null;
    const attribution = getAttribution();
    buffer.push({
      event_name: eventName,
      user_id: userId,
      session_id: getSessionId(),
      platform: detectPlatform(),
      props: sanitize(props),
      attribution: Object.keys(attribution).length ? attribution : undefined,
    });
    if (buffer.length >= BUFFER_LIMIT) {
      flush();
    } else {
      scheduleFlush();
    }
  } catch {
    /* never throw from analytics */
  }
};

// Strip potential PII — keep only primitive values under 120 chars
const sanitize = (obj) => {
  if (!obj || typeof obj !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v == null) continue;
    if (typeof v === 'number' || typeof v === 'boolean') { out[k] = v; continue; }
    if (typeof v === 'string') { out[k] = v.slice(0, 120); continue; }
    // Drop anything else (objects, arrays) — keeps payload small & safe
  }
  return out;
};

// Flush on page unload so we don't lose events
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    try {
      const token = localStorage.getItem('token');
      const events = buffer.splice(0, buffer.length);
      if (events.length > 0 && navigator.sendBeacon) {
        // sendBeacon is fire-and-forget and survives page unload
        const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' });
        navigator.sendBeacon(`${API}/analytics/events`, blob);
      } else if (events.length > 0) {
        const attrHeader = getAttributionHeaderValue();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        if (attrHeader) headers['x-sfy-attribution'] = attrHeader;
        fetch(`${API}/analytics/events`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ events }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch { /* ignore */ }
  });
}

// Clear session id when user logs out so a new user gets a fresh session
export const resetAnalyticsSession = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
};
